/**
 * Item type definitions for Idle Depths.
 *
 * Items are the primary collectible objects in the game,
 * including weapons, armor, resources, consumables, and quest items.
 */

/**
 * Item type categories.
 */
export enum ItemType {
  WEAPON = 'weapon',
  ARMOR = 'armor',
  RESOURCE = 'resource',
  CONSUMABLE = 'consumable',
  QUEST = 'quest',
  MISC = 'misc',
}

/**
 * Item rarity tiers.
 */
export enum ItemRarity {
  COMMON = 'common',
  UNCOMMON = 'uncommon',
  RARE = 'rare',
  EPIC = 'epic',
  LEGENDARY = 'legendary',
}

/**
 * Equipment slot types.
 *
 * Defines where an item can be equipped on a character.
 */
export enum EquipmentSlot {
  HEAD = 'head',
  BODY = 'body',
  LEGS = 'legs',
  FEET = 'feet',
  HANDS = 'hands',
  MAIN_HAND = 'mainHand',
  OFF_HAND = 'offHand',
  NECK = 'neck',
  RING = 'ring',
  AMMO = 'ammo',
}

/**
 * Equipment data stored on character.
 *
 * Maps equipment slots to item IDs (null if empty).
 */
export type CharacterEquipment = {
  [key in EquipmentSlot]: string | null;
};

/**
 * Create empty equipment object.
 *
 * @returns Empty equipment with all slots null
 */
export function createEmptyEquipment(): CharacterEquipment {
  return {
    [EquipmentSlot.HEAD]: null,
    [EquipmentSlot.BODY]: null,
    [EquipmentSlot.LEGS]: null,
    [EquipmentSlot.FEET]: null,
    [EquipmentSlot.HANDS]: null,
    [EquipmentSlot.MAIN_HAND]: null,
    [EquipmentSlot.OFF_HAND]: null,
    [EquipmentSlot.NECK]: null,
    [EquipmentSlot.RING]: null,
    [EquipmentSlot.AMMO]: null,
  };
}

/**
 * Item definition registered via content packs.
 *
 * Defines the static properties of an item type.
 */
export interface ItemDefinition {
  /** Unique item identifier */
  id: string;

  /** Display name */
  name: string;

  /** Full description */
  description: string;

  /** Item category */
  type: ItemType;

  /** Rarity tier */
  rarity: ItemRarity;

  /** Icon identifier (Lucide icon name or asset path) */
  icon: string;

  /** Base gold value */
  value: number;

  /** Whether multiple items can occupy one slot */
  stackable: boolean;

  /** Maximum stack size (null = unlimited) */
  maxStackSize: number | null;

  /** Whether item can be sold to shops */
  tradeable: boolean;

  /**
   * Equipment slot for equippable items (null if not equippable).
   * Only weapons and armor can typically be equipped.
   */
  equipSlot: EquipmentSlot | null;

  /** Type-specific properties (damage, heal amount, etc.) */
  properties: Record<string, unknown>;
}

/**
 * Item instance stored in bank or inventory.
 */
export interface BankItem {
  /** References ItemDefinition.id */
  itemId: string;

  /** Quantity of this item */
  quantity: number;

  /** When the item was first acquired (Unix timestamp ms) */
  acquiredAt: number;

  /** When the item was last modified (Unix timestamp ms) - for sync conflict resolution */
  updatedAt: number;
}

/**
 * Type guard for ItemDefinition.
 *
 * @param item - Unknown value to check
 * @returns True if item is a valid ItemDefinition
 */
export function isItemDefinition(item: unknown): item is ItemDefinition {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as ItemDefinition).id === 'string' &&
    'name' in item &&
    typeof (item as ItemDefinition).name === 'string' &&
    'type' in item &&
    Object.values(ItemType).includes((item as ItemDefinition).type) &&
    'value' in item &&
    typeof (item as ItemDefinition).value === 'number'
  );
}

/**
 * Result of adding an item to the bank.
 */
export interface AddItemResult {
  /** Whether the item was successfully added */
  success: boolean;

  /** Error message if failed (only present when success is false or partial success) */
  error?: string | undefined;

  /** How many items were actually added (may differ from requested if at capacity) */
  addedQuantity: number;
}

/**
 * Result of an equip/unequip operation.
 */
export interface EquipResult {
  /** Whether the operation succeeded */
  success: boolean;

  /** Error message if failed */
  error?: string | undefined;

  /** Item that was previously equipped (null if slot was empty) */
  previousItemId: string | null;
}
